import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {Component, DestroyRef, ElementRef, inject, Inject, OnInit, ViewChild} from '@angular/core';
import { FormGroup, UntypedFormControl } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import { MatChipInputEvent} from '@angular/material/chips';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { AuthService } from '../../services/auth.service';
import { EmailService } from '../../services/email.service';
import { FileAttachment } from '../models/attachment';
import { CommunicationLog } from '../models/communication-log';
import { EmailModel } from '../models/email-model';
import { EmailRecipient } from '../models/email-recipient';
import { FieldSupervisor } from '../models/fsupervisor.model';
import { AlertType } from '../models/alertType';
import { FSContactsService } from 'src/app/services/fs-contacts.service';
import { FormBuilder, Validators } from '@angular/forms';
import { InternService } from 'src/app/services/intern.service';
import { InternCampusAdministrators } from '../models/intern-campus-administrators.model';
import { NewContact } from '../models/new-contact.model';
import { RecipientTypeEnum } from '../enums/recipient-type.enum';
import { ContactTypeEnum } from '../enums/contact-type.enum';
import { SelectedIntern } from '../models/selected-intern.model';
import { ContactService } from 'src/app/services/contact.service';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import { firstValueFrom } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-email',
  templateUrl: './email.component.html',
  styleUrls: ['./email.component.scss']
})
export class EmailComponent implements OnInit {

  visible = true;
  selectable = true;
  removable = true;
  addOnBlur = true;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  recipientCtrl = new UntypedFormControl();
  filteredRecipients: EmailRecipient[];
  supervisor: FieldSupervisor;
  mailData: EmailModel;
  token: string;
  log: CommunicationLog[] = [];

  @ViewChild('recipientInput') recipientInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;
  subject = new UntypedFormControl('');
  priority = new UntypedFormControl();
  emailContent = new UntypedFormControl('');
  htmlContent = '';
  fileInput: any;
  invalidAttachment = false;
  maxFileSizeAllowed = 50000000;
  currentfileSize: number = 0;
  invalidFileSize = false;
  attachments = [];
  fileList = '';
  maxFileList = '';
  alertTypeList: AlertType[] = [];
  alertForm: FormGroup;
  principalCtrl = new UntypedFormControl(false);
  mentorCtrl = new UntypedFormControl(false);
  campusSupervisorCtrl = new UntypedFormControl(false);
  internCampusAdministratorsList: InternCampusAdministrators[] = [];
  isSending = false;
  emailSent = false;
  skippedRecipients: { internId: string; internName: string; issues: { contactType: string; reason: 'missing-email' | 'missing-contact' }[] }[] = [];
  newContact: NewContact;
  selectedInterns: SelectedIntern[];
  fieldSupervisor: FieldSupervisor;
  private destroyRef = inject(DestroyRef)

  constructor(public dialogRef: MatDialogRef<EmailComponent>, @Inject(MAT_DIALOG_DATA) public data: any,
    private emailService: EmailService, private authService: AuthService,
    private fsContactsService: FSContactsService, private fb: FormBuilder,
    private internService: InternService, private contactService: ContactService,
    private snackBar: MatSnackBar) {
    //initialize mail data
    this.data.sent = false;
    this.mailData = data.mailModel;
    this.selectedInterns = data.selectedInterns;
    if (data.internCampusAdministrators != null) {
      this.internCampusAdministratorsList = [data.internCampusAdministrators];
    }
    this.filteredRecipients = this.mailData.bcc;
    this.fieldSupervisor = data.fieldSupervisor;
  }

  ngOnInit() {
    //get auth token from authService
    this.authService.getAccessToken().then(token => {
      this.token = token;
    });

    this.alertForm = this.fb.group({
      alertType: [null, Validators.required],
      details: ['', [Validators.required, Validators.minLength(1)]]
    });

    this.fsContactsService.GetAlertTypes().subscribe(data => {
      this.alertTypeList = data;
    });
  }

  close() {
    if (!this.isSending) {
      this.dialogRef.close();
    }
  }

  add(event: MatChipInputEvent): void {
    // Add recipient only when MatAutocomplete is not open
    // To make sure this does not conflict with OptionSelected Event
    if (!this.matAutocomplete.isOpen) {
      const input = event.input;
      const value = event.value;

      // Add a recipient
      if ((value || '').trim()) {
        //this.recipients.push(value.trim());
      }

      // Reset the input value
      if (input) {
        input.value = '';
      }

      this.recipientCtrl.setValue(null);
    }
  }

  remove(recipient: EmailRecipient): void {
    const index = this.mailData.allRecipients.indexOf(recipient);
    if (index >= 0) {
      this.mailData.allRecipients.splice(index, 1);
    }
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    //this.recipients.push(event.option.viewValue);
    this.recipientInput.nativeElement.value = '';
    this.recipientCtrl.setValue(null);
  }

  private _filter(value: EmailRecipient): EmailRecipient[] {
    const filterValue = value.name.toLowerCase();

    return this.mailData.allRecipients.filter(recipient => recipient.name.toLowerCase().indexOf(filterValue) === 0);
  }

  public async sendEmails() {

    // Check if the alert form is valid
    if (this.alertForm.invalid) {
      // Mark all controls as touched to trigger validation messages
      this.alertForm.markAllAsTouched();
      return;
    }

    let self = this;

    this.isSending = true;

    let newMailData = new EmailModel;
    newMailData.bcc = this.mailData.bcc.filter(r => !!r.email?.trim());
    newMailData.cc = (this.mailData.cc ?? []).filter(r => !!r.email?.trim());
    newMailData.body = this.mailData.body;

    //Populate data from
    newMailData.subject = this.subject.value;
    newMailData.htmlContent = this.htmlContent;
    newMailData.highImportance = this.priority.value;
    newMailData.allRecipients = [];

    newMailData.saveToSentFolder = true;
    newMailData.processReplacements = true;
    newMailData.emailPerReceipient = true;

    //Add Attachments
    if (this.attachments != null && this.attachments.length > 0) {
      newMailData.hasAttachments = true;
      newMailData.attachments = [];
      for (var i = 0; i < self.attachments.length; i++) {

        let fileSource = self.attachments[i];

        let attachment = new FileAttachment();
        attachment.size = fileSource.size;
        attachment.contentType = fileSource.type;

        attachment.contentBytes = await this.readUploadedFileAsText(fileSource);

        attachment.name = fileSource.name;
        attachment.lastModifiedDateTime = fileSource.lastModifiedDate;
        attachment.contentId = fileSource.name;
        newMailData.attachments.push(attachment);
      }
    }

    if ((this.principalCtrl.value || this.mentorCtrl.value || this.campusSupervisorCtrl.value)
        && this.internCampusAdministratorsList.length === 0) {
      try {
        const internIds = this.selectedInterns.map(i => i.internId);
        this.internCampusAdministratorsList = await firstValueFrom(
          this.internService.getInternCampusAdministrators(internIds)
        );
      } catch {
        this.isSending = false;
        return;
      }
    }

    if (this.principalCtrl.value) {
      this.internCampusAdministratorsList.forEach(admin => {
        if (admin?.principalEmailAddress?.trim()) {
          var principalEmailRecipient = new EmailRecipient();
          principalEmailRecipient.email = admin.principalEmailAddress;
          principalEmailRecipient.name = admin.principalName;
          principalEmailRecipient.profileId = admin.profileId;
          if (!newMailData.bcc.some(recipient => recipient.email === principalEmailRecipient.email)) {
            newMailData.bcc.push(principalEmailRecipient);
          }
        }
      });
    }

    if (this.mentorCtrl.value) {
      this.internCampusAdministratorsList.forEach(admin => {
        if (admin?.mentorEmailAddress?.trim()) {
          var mentorEmailRecipient = new EmailRecipient();
          mentorEmailRecipient.email = admin.mentorEmailAddress;
          mentorEmailRecipient.name = admin.mentorName;
          mentorEmailRecipient.profileId = admin.profileId;
          if (!newMailData.bcc.some(recipient => recipient.email === mentorEmailRecipient.email)) {
            newMailData.bcc.push(mentorEmailRecipient);
          }
        }
      });
    }

    if (this.campusSupervisorCtrl.value) {
      this.internCampusAdministratorsList.forEach(admin => {
        if (admin?.campusSupervisorEmailAddress?.trim()) {
          var campusSupervisorEmailRecipient = new EmailRecipient();
          campusSupervisorEmailRecipient.email = admin.campusSupervisorEmailAddress;
          campusSupervisorEmailRecipient.name = admin.campusSupervisorName;
          campusSupervisorEmailRecipient.profileId = admin.profileId;
          if (!newMailData.bcc.some(recipient => recipient.email === campusSupervisorEmailRecipient.email)) {
            newMailData.bcc.push(campusSupervisorEmailRecipient);
          }
        }
      });
    }

    //validate if there is a valid token to send the email
    if (this.token.length > 0) {
      //Calling service to send emails
      this.emailService.SendEmail(newMailData, this.token).subscribe({
        next: data => {
          this.createNewContact();
          const skipped = this.collectSkippedRecipients();
          if (skipped.length === 0) {
            this.dialogRef.close(data);
          } else {
            this.skippedRecipients = skipped;
            this.emailSent = true;
          }
        },
        error: () => {
          this.isSending = false;
          this.snackBar.open('Failed to send email. Please try again.', 'Dismiss', {
            panelClass: 'custom-snackbar'
          });
        },
        complete: () => {
          this.isSending = false;
        }
      });
    } else {
      this.isSending = false;
    }
  }

  closeAfterSend() {
    this.dialogRef.close(true);
  }

  private collectSkippedRecipients(): { internId: string; internName: string; issues: { contactType: string; reason: 'missing-email' | 'missing-contact' }[] }[] {
    const map = new Map<string, { internName: string; issues: { contactType: string; reason: 'missing-email' | 'missing-contact' }[] }>();

    const addIssue = (internId: string, internName: string, contactType: string, reason: 'missing-email' | 'missing-contact') => {
      if (!map.has(internId)) {
        map.set(internId, { internName, issues: [] });
      }
      map.get(internId).issues.push({ contactType, reason });
    };

    this.mailData.bcc
      .filter(r => !r.email?.trim())
      .forEach(r => {
        const intern = this.selectedInterns.find(s => s.profileId === r.profileId);
        addIssue(intern?.internId ?? '', r.name, 'Intern', 'missing-email');
      });

    if (this.principalCtrl.value) {
      this.selectedInterns.forEach(intern => {
        const admin = this.internCampusAdministratorsList.find(a => a.profileId === intern.profileId);
        if (!admin?.principalName?.trim()) {
          addIssue(intern.internId, intern.internFullName, 'Principal', 'missing-contact');
        } else if (!admin.principalEmailAddress?.trim()) {
          addIssue(intern.internId, intern.internFullName, 'Principal', 'missing-email');
        }
      });
    }

    if (this.mentorCtrl.value) {
      this.selectedInterns.forEach(intern => {
        const admin = this.internCampusAdministratorsList.find(a => a.profileId === intern.profileId);
        if (!admin?.mentorName?.trim()) {
          addIssue(intern.internId, intern.internFullName, 'Mentor', 'missing-contact');
        } else if (!admin.mentorEmailAddress?.trim()) {
          addIssue(intern.internId, intern.internFullName, 'Mentor', 'missing-email');
        }
      });
    }

    if (this.campusSupervisorCtrl.value) {
      this.selectedInterns.forEach(intern => {
        const admin = this.internCampusAdministratorsList.find(a => a.profileId === intern.profileId);
        if (!admin?.campusSupervisorName?.trim()) {
          addIssue(intern.internId, intern.internFullName, 'Campus Supervisor', 'missing-contact');
        } else if (!admin.campusSupervisorEmailAddress?.trim()) {
          addIssue(intern.internId, intern.internFullName, 'Campus Supervisor', 'missing-email');
        }
      });
    }

    return Array.from(map.entries()).map(([internId, { internName, issues }]) => ({ internId, internName, issues }));
  }

  createNewContact() {
    let contactRecipients = [];
    contactRecipients.push(RecipientTypeEnum.Intern);
    if (this.mentorCtrl.value && this.internCampusAdministratorsList.some(a => a?.mentorEmailAddress != null)) {
      contactRecipients.push(RecipientTypeEnum.Mentor);
    }
    if (this.principalCtrl.value && this.internCampusAdministratorsList.some(a => a?.principalEmailAddress != null)) {
      contactRecipients.push(RecipientTypeEnum.Principal);
    }
    if (this.campusSupervisorCtrl.value && this.internCampusAdministratorsList.some(a => a?.campusSupervisorEmailAddress != null)) {
      contactRecipients.push(RecipientTypeEnum.CampusSupervisor);
    }

    this.newContact = {
      contactType: ContactTypeEnum.Email,
      alertType: this.alertForm.value.alertType,
      contactRecipients: contactRecipients,
      contactDate: new Date(),
      details: this.alertForm.value.details
    }
    this.contactService.onSubmit(this.newContact, this.selectedInterns, this.fieldSupervisor, this.token).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  config: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: '150px',
    minHeight: '100px',
    maxHeight: '150px',
    width: 'auto',
    minWidth: '0',
    translate: 'yes',
    enableToolbar: true,
    showToolbar: true,
    placeholder: 'Enter text here...',
    defaultParagraphSeparator: '',
    defaultFontName: 'arial',
    defaultFontSize: '2',
    fonts: [
      { class: 'arial', name: 'Arial' },
      { class: 'times-new-roman', name: 'Times New Roman' },
      { class: 'calibri', name: 'Calibri' },
      { class: 'comic-sans-ms', name: 'Comic Sans MS' }
    ],
    uploadWithCredentials: true,
    sanitize: true,
    toolbarPosition: 'top',
    toolbarHiddenButtons: [
      [
        'strikeThrough',
        'subscript',
        'superscript'
      ],
      [
        'textColor',
        'backgroundColor',
        'customClasses',
        'link',
        'unlink',
        'insertVideo',
        'insertHorizontalRule',
        'removeFormat',
        'toggleEditorMode'
      ]
    ],

  };

  public fileSelectedChange(event: any) {
    this.currentfileSize = 0;
    this.invalidAttachment = false;
    this.invalidFileSize = false;
    this.fileInput = event.target;

    this.validateFilesSize(this.fileInput.files);

    if (this.attachments.length > 4) {
      this.invalidAttachment = true;
    }
  }

  private validateFilesSize(files: any) {

    for (var i = 0; i < files.length; i++) {

      this.attachments.push(files[i]);

      this.currentfileSize = this.currentfileSize + files[i].size;

      if (this.attachments.length > 4) {
        this.fileList = `${this.maxFileList} ( ${(this.attachments.length - 4)} ) files more...`;
      }
      else if (this.attachments.length == 4) {
        this.fileList = this.fileList + files[i].name + ' | ';
        this.maxFileList = this.fileList;
      } else {
        this.fileList = this.fileList + files[i].name + ' | ';
      }

      if (files[i].size > this.maxFileSizeAllowed) {
        this.invalidFileSize = true;
      }
    }

    if (this.currentfileSize >= this.maxFileSizeAllowed) {
      this.invalidFileSize = true;
    }
  }

  private readUploadedFileAsText = (inputFile) => {
    const temporaryFileReader = new FileReader();

    return new Promise((resolve, reject) => {
      temporaryFileReader.onerror = () => {
        temporaryFileReader.abort();
        reject(new DOMException("Problem parsing input file."));
      };

      temporaryFileReader.onload = () => {
        var b64 = temporaryFileReader.result.toString().replace(/^data:.+;base64,/, '');
        resolve(b64);
      };
      temporaryFileReader.readAsDataURL(inputFile);
    });
  };
}
